import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heading, Text, Button, Card, Callout, Spinner } from 'frosted-ui';
import {
  InfoCircle16,
  MobilePhone16,
  Checkmark16,
  ArrowUpRightFromSquare16,
  Rotate16,
} from '@frosted-ui/icons';
import { useCompanyId } from '../../contexts/CompanyContext';
import { useProductCatalog, isFreeProduct } from '../../hooks/useProductCatalog';
import { variantLabel } from '../../utils/variants';
import { api } from '../../services/api';
import type { HeldInWhopSpot } from '../../types';
import LoadFailed from '../LoadFailed';
import MemberOfferCard from '../MemberOfferCard';
import type { InWhopDraft, WizardData } from './wizardTypes';

interface StepPlaceInWhopProps {
  data: WizardData;
  inWhop: InWhopDraft;
  onChange: (updates: Partial<InWhopDraft>) => void;
  /** Switch to a link test; absent once the draft exists (its path is fixed). */
  onUseLink?: () => void;
}

/** What a free member sees in the Upgrade tab, for either variant. */
export function MemberOfferPreview({
  data,
  freeProductName,
}: {
  data: WizardData;
  freeProductName: string;
}) {
  const companyId = useCompanyId();
  const [shown, setShown] = useState(data.variants[0]?.name ?? 'A');
  const variant = data.variants.find((v) => v.name === shown) ?? data.variants[0];
  // What the member really sees; until it loads (or if it can't), nothing is
  // better than placeholder words in the seller's preview.
  const [real, setReal] = useState<{ sellerName: string | null; benefits: string[] } | null>(null);
  useEffect(() => {
    if (!data.productId) return;
    let cancelled = false;
    api
      .getMemberPreview(companyId, data.productId)
      .then((preview) => {
        if (!cancelled) setReal(preview);
      })
      .catch((err: unknown) => console.error('Failed to load the member preview:', err));
    return () => {
      cancelled = true;
    };
  }, [companyId, data.productId]);
  if (!variant) return null;

  return (
    <Card size="2">
      <Text size="1" color="gray" weight="medium" style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        Member preview
      </Text>
      <div
        style={{
          marginTop: 'var(--space-3)',
          borderRadius: 'var(--radius-4)',
          border: '1px solid var(--gray-a6)',
          overflow: 'hidden',
          backgroundColor: 'var(--gray-a2)',
        }}
      >
        <div style={{ padding: 'var(--space-2) var(--space-3)', borderBottom: '1px solid var(--gray-a5)' }}>
          <Text size="1" color="gray">
            {freeProductName || 'Your free product'} · Upgrade
          </Text>
        </div>
        <div style={{ padding: 'var(--space-3)' }}>
          <MemberOfferCard
            sellerName={real?.sellerName}
            productName={data.productName || 'your paid product'}
            benefits={real?.benefits}
            price={variant.price}
            currency={data.currency}
            interval={variant.interval}
            trialDays={variant.trialDays}
            initialFee={variant.hasInitialFee ? variant.initialFee : null}
            action={
              <Button size="3" variant="solid" disabled style={{ width: '100%' }}>
                Upgrade
              </Button>
            }
          />
        </div>
      </div>
      <div role="group" aria-label="Preview price" style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
        {data.variants.map((v) => (
          <Button
            key={v.name}
            type="button"
            size="2"
            style={{ flex: 1 }}
            variant={shown === v.name ? 'solid' : 'surface'}
            color={shown === v.name ? undefined : 'gray'}
            aria-pressed={shown === v.name}
            onClick={() => setShown(v.name)}
          >
            {variantLabel(v.name)}
          </Button>
        ))}
      </div>
    </Card>
  );
}

export function AddAppInstructions({ freeProductName }: { freeProductName: string }) {
  const companyId = useCompanyId();
  const name = freeProductName || 'your free product';
  return (
    <Card size="2">
      <Heading size="3" style={{ marginBottom: 'var(--space-2)' }}>
        Add Paywall Lab to {name}
      </Heading>
      <Text size="2" color="gray" style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
        It isn't there yet, so add it by hand. It takes one minute, and we'll tick it off here once
        it's there.
      </Text>
      <ol style={{ margin: 0, paddingLeft: 'var(--space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        <li><Text size="2">Open your Whop dashboard → Products → {name}</Text></li>
        <li><Text size="2">Choose Add app, pick Paywall Lab and name the tab “Upgrade”</Text></li>
        <li><Text size="2">Save. Free members now see one price — always the same one for each member</Text></li>
      </ol>
      <Button asChild variant="soft" size="2" style={{ marginTop: 'var(--space-4)' }}>
        <a href={`https://whop.com/dashboard/${companyId}/`} target="_blank" rel="noopener noreferrer">
          Open Whop dashboard
          <ArrowUpRightFromSquare16 />
        </a>
      </Button>
    </Card>
  );
}

export default function StepPlaceInWhop({ data, inWhop, onChange, onUseLink }: StepPlaceInWhopProps) {
  const companyId = useCompanyId();
  const { catalog, loading, error, reload } = useProductCatalog(companyId);
  const [held, setHeld] = useState<HeldInWhopSpot[]>([]);
  useEffect(() => {
    let cancelled = false;
    api
      .getHeldInWhopSpots(companyId)
      .then((spots) => {
        if (!cancelled) setHeld(spots);
      })
      // Not knowing isn't fatal: creating the spot still answers with a 409.
      .catch((err: unknown) => console.error('Failed to load which free products are in use:', err));
    return () => {
      cancelled = true;
    };
  }, [companyId]);
  const heldBy = (productId: string) => held.find((h) => h.productId === productId);
  const freeProducts = catalog.filter(
    (e) => isFreeProduct(e) && e.product.id !== data.productId
  );
  const hasOneTime = data.variants.some((v) => v.interval === 'one_time');

  // Default to the first free product nothing is running in. A product that
  // turns out to be taken, or a list that failed to load, doesn't stay selected.
  const available = freeProducts.filter((e) => !heldBy(e.product.id));
  const firstFree = available[0];
  const selectedHeld = Boolean(inWhop.freeProductId && heldBy(inWhop.freeProductId));
  const loadFailed = Boolean(error) && freeProducts.length === 0;
  useEffect(() => {
    if (loadFailed || selectedHeld) {
      if (inWhop.freeProductId) onChange({ freeProductId: '', freeProductName: '' });
      return;
    }
    if (!inWhop.freeProductId && firstFree) {
      onChange({ freeProductId: firstFree.product.id, freeProductName: firstFree.product.name });
    }
  }, [inWhop.freeProductId, firstFree, selectedHeld, loadFailed, onChange]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <div>
        <Heading size="5" style={{ marginBottom: 'var(--space-2)' }}>
          Which free product are your members in?
        </Heading>
        <Text size="2" color="gray">
          Members of it get an “Upgrade” tab. Each free member sees one price, always the same one,
          and can buy without leaving Whop.
        </Text>
      </div>

      <fieldset style={{ border: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        <legend
          style={{
            padding: 0,
            marginBottom: 'var(--space-2)',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--space-2)',
          }}
        >
          <Text size="2" weight="medium">Free product</Text>
          <Button type="button" variant="ghost" size="1" color="gray" onClick={reload} disabled={loading}>
            <Rotate16 />
            Refresh
          </Button>
        </legend>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <Spinner size="2" />
            <Text size="2" color="gray">Loading your products…</Text>
          </div>
        ) : loadFailed ? (
          <LoadFailed what="your products" onRetry={reload} />
        ) : freeProducts.length === 0 ? (
          <Callout.Root color="yellow" variant="surface">
            <Callout.Icon><InfoCircle16 /></Callout.Icon>
            <Callout.Text>
              You don't have a free product yet. Create one in Whop (a free community, channel or
              guide), then press Refresh{onUseLink ? ', or test with a link you share instead' : ''}.
            </Callout.Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
              <Button asChild size="2" variant="soft" className="pl-tap">
                <a href={`https://whop.com/dashboard/${companyId}/`} target="_blank" rel="noopener noreferrer">
                  Open Whop dashboard
                  <ArrowUpRightFromSquare16 />
                </a>
              </Button>
              <Button type="button" size="2" variant="soft" color="gray" className="pl-tap" onClick={reload}>
                <Rotate16 />
                Refresh
              </Button>
              {onUseLink && (
                <Button type="button" size="2" variant="ghost" className="pl-tap" onClick={onUseLink}>
                  Use a link instead
                </Button>
              )}
            </div>
          </Callout.Root>
        ) : (
          freeProducts.map((e) => {
            const selected = inWhop.freeProductId === e.product.id;
            const holder = heldBy(e.product.id);
            // One free product shows one test, so a taken one can't be picked.
            if (holder) {
              return (
                <div
                  key={e.product.id}
                  style={{
                    boxSizing: 'border-box',
                    minHeight: 56,
                    padding: 'var(--space-3) var(--space-4)',
                    borderRadius: 'var(--radius-3)',
                    border: '2px solid var(--gray-a4)',
                    backgroundColor: 'var(--gray-a2)',
                  }}
                >
                  <Text size="2" weight="medium" style={{ display: 'block', overflowWrap: 'anywhere' }}>
                    {e.product.name}
                  </Text>
                  <Text size="1" color="gray" style={{ display: 'block', overflowWrap: 'anywhere' }}>
                    “{holder.name}” is running here, and a free product shows one test at a time.
                    Pause it to use this one.{' '}
                    <Link to={`/experiments/${holder.experimentId}`}>Open it</Link>
                  </Text>
                </div>
              );
            }
            return (
              <button
                key={e.product.id}
                type="button"
                aria-pressed={selected}
                onClick={() => onChange({ freeProductId: e.product.id, freeProductName: e.product.name })}
                className="vara2-card-hover"
                style={{
                  all: 'unset',
                  boxSizing: 'border-box',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  minHeight: 56,
                  padding: 'var(--space-3) var(--space-4)',
                  borderRadius: 'var(--radius-3)',
                  border: selected ? '2px solid var(--accent-9)' : '2px solid var(--gray-a5)',
                  backgroundColor: selected ? 'var(--accent-a2)' : 'var(--gray-a2)',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    flexShrink: 0,
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    boxSizing: 'border-box',
                    border: selected ? '6px solid var(--accent-9)' : '2px solid var(--gray-a8)',
                  }}
                />
                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, overflowWrap: 'anywhere' }}>
                  <Text size="2" weight="medium">{e.product.name}</Text>
                  <Text size="1" color="gray">Free</Text>
                </span>
                {selected && <Checkmark16 style={{ marginLeft: 'auto', flexShrink: 0, color: 'var(--accent-11)' }} />}
              </button>
            );
          })
        )}
      </fieldset>

      {hasOneTime && (
        <Callout.Root color="yellow" variant="surface">
          <Callout.Icon><MobilePhone16 /></Callout.Icon>
          <Callout.Text>
            <strong>Testing a one-time price?</strong> On Whop's iPhone app, one-time purchases under
            $700 go through Apple at a higher price, so iPhone buyers may see a different amount.
            Monthly and yearly prices aren't affected.
          </Callout.Text>
        </Callout.Root>
      )}

      {available.length > 0 && (
        <Text size="2" color="gray">
          When you continue, we add an “Upgrade” tab to {inWhop.freeProductName || 'the product'}.
          If Whop needs you to do it yourself, we'll show you how.
        </Text>
      )}
    </div>
  );
}
